import { Recurrence } from '@tableau/self-service-schedules';
import { ButtonType, ButtonWidget, CheckBoxWithLabelWidget, InlineAlertType, InlineAlertWidget, IntegerStepperWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { GeocodingJob, GeocodingJobStatus } from '~src/geocoding/requests/GeocodingBackgrounderRequests';

import { GeocodingSchedule } from './GeocodingSchedule';

interface GeocodeTabProps {
  tokenLimit: number;
  scheduleRecurrence: Recurrence;
  isScheduleChecked: boolean;
  columnNames?: string[];
  handleTokenLimitChange: (limit: number) => void;
  handleScheduleChange: (recurrence: Recurrence) => void;
  handleEstimateJob: () => void;
  lastEstimationJob?: GeocodingJob;
  lastEstimation?: number;
}

interface GeocodeTabState {
  tokenLimit: number;
  isScheduleChecked: boolean;
}

export class GeocodeTab extends React.PureComponent<GeocodeTabProps, GeocodeTabState> {
  public constructor(props: GeocodeTabProps) {
    super(props);
    this.state = {
      tokenLimit: props.tokenLimit,
      isScheduleChecked: props.isScheduleChecked,
    };
  }

  private handleTokenLimitChange = (limit: number): void => {
    this.setState({tokenLimit: limit});
    this.props.handleTokenLimitChange(limit);
  };

  private buildTokenLimitArea(): React.ReactNode {
    return (
      <>
        <IntegerStepperWidget
          label={Messages.geocodingConfigTokenLimitStepperLabel()}
          max={50000}
          min={0}
          start={this.state.tokenLimit}
          onValueUpdated={this.handleTokenLimitChange}
          testId='geocode-row-count'
        />
        <div className='geocodingInfoTextBlock'>
          <div className='geocodingBoldText'>{Messages.geocodingConfigGeocodeTabInfoTitle()}</div>
          <div>{Messages.geocodingConfigGeocodeTabInfoParagraph()}</div>
        </div>
        {this.getInlineAlert()}
      </>
    );
  }

  private getInlineAlert(): React.ReactNode {
    // TODO: move all strings to Messages to be localized once design and verbiage are finalzied.
    let alertText = Messages.geocodingConfigTokenLimitInfo();
    let buttonText = 'Estimate';
    let showEstimateButton = true;

    if (this.props.lastEstimationJob && (this.props.lastEstimationJob.status === GeocodingJobStatus.Pending || this.props.lastEstimationJob.status === GeocodingJobStatus.InProgress)) {
      alertText = `Estimation in progress.`;
      showEstimateButton = false;
    } else if (this.props.lastEstimationJob && (this.props.lastEstimationJob.status === GeocodingJobStatus.Cancelled || this.props.lastEstimationJob.status === GeocodingJobStatus.Failed)) {
      alertText = 'The last estimate attempt failed or was cancelled. Please try again.';
      buttonText = 'Re-estimate';
    } else if (this.props.lastEstimationJob && this.props.lastEstimation) {
      alertText = `The last estimate result is: ${this.props.lastEstimation} ${this.props.lastEstimation === 1 ? 'row' : 'rows'}.`;
      buttonText = 'Re-estimate';
    }

    return (
      <InlineAlertWidget
        button={showEstimateButton ? <ButtonWidget
          testId="geocoding-estimate"
          className={'geocodingEstimateButton'}
          buttonType={ButtonType.Outline}
          children={buttonText}
          handleClick={() => this.props.handleEstimateJob()}
        /> : undefined}
        className='geocodingTokenLimitInfo'
        type={InlineAlertType.Info}
        testId='geocodingTokenLimitInfo'>
          {alertText}
      </InlineAlertWidget>
    );
  }

  private buildScheduleCheckbox(): React.ReactNode {
    return (
      <>
        <CheckBoxWithLabelWidget
          checked={this.state.isScheduleChecked}
          handleChange={(checked: boolean) => this.setState({ isScheduleChecked: checked })}
          testId='geocoding-schedule'
          label={Messages.geocodingConfigGeocodeTabScheduleCheckboxLabel()}
          className='geocodingBoldText'
        />
        {this.state.isScheduleChecked &&
          <GeocodingSchedule scheduleRecurrence={this.props.scheduleRecurrence} handleScheduleChange={this.props.handleScheduleChange} />
        }
      </>
    );
  }

  public render(): React.ReactNode {
    if (!this.props.columnNames) {
      return <div>{Messages.geocodingConfigGeocodeTabNoSelection()}</div>;
    }

    return (
      <div>
        <div className='geocodingLargeText'>{Messages.geocodingConfigGeocodeTabHeader()}</div>
        {this.buildTokenLimitArea()}
        {this.buildScheduleCheckbox()}
      </div>
    );
  }
}
